import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, MapPin, Gauge, X, RefreshCw, Bell, BellOff, Clock
} from 'lucide-react';
import { toast } from 'sonner';

export default function PilotAlertsPanel({ eventId, eventTitle }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchAlerts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/rally-master/events/${eventId}/alerts?active_only=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAlerts(response.data.alerts || []);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchAlerts();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, fetchAlerts]);

  const dismissAlert = async (alertId) => {
    setDismissing(alertId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/rally-master/alerts/${alertId}/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      toast.success('Alerta desactivada');
    } catch (error) {
      console.error('Error dismissing alert:', error);
      toast.error('Error al desactivar alerta');
    } finally {
      setDismissing(null);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const getAlertIcon = (type) => {
    if (type === 'deviation') {
      return <MapPin className="w-5 h-5 text-red-500" />;
    }
    if (type === 'return_to_track') {
      return <MapPin className="w-5 h-5 text-green-500" />;
    }
    if (type === 'speed_normalized') {
      return <Gauge className="w-5 h-5 text-green-500" />;
    }
    return <Gauge className="w-5 h-5 text-orange-500" />;
  };

  const getAlertBadge = (type) => {
    if (type === 'deviation') {
      return <Badge variant="destructive">Desviación</Badge>;
    }
    if (type === 'return_to_track') {
      return <Badge className="bg-green-500 text-white">Volvió al track</Badge>;
    }
    if (type === 'speed_normalized') {
      return <Badge className="bg-green-500 text-white">Velocidad OK</Badge>;
    }
    return <Badge className="bg-orange-500">Velocidad</Badge>;
  };

  // Count only active alerts (not return/normalized notifications)
  const activeAlertsCount = alerts.filter(a => a.is_active).length;

  return (
    <Card className={`${activeAlertsCount > 0 ? 'border-red-300 bg-red-50' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${activeAlertsCount > 0 ? 'text-red-500 animate-pulse' : 'text-gray-400'}`} />
            Alertas de Pilotos
            {activeAlertsCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {activeAlertsCount} activas
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              title={autoRefresh ? 'Desactivar auto-refresh' : 'Activar auto-refresh'}
            >
              {autoRefresh ? (
                <Bell className="w-4 h-4 text-green-500" />
              ) : (
                <BellOff className="w-4 h-4 text-gray-400" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchAlerts}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Última actualización: {lastRefresh.toLocaleTimeString('es-ES')}
          {autoRefresh && ' (auto-refresh cada 60s)'}
        </p>
      </CardHeader>
      
      <CardContent>
        {loading && alerts.length === 0 ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500 mx-auto"></div>
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No hay alertas activas</p>
            <p className="text-xs mt-1">Las alertas aparecerán aquí cuando un piloto se desvíe o exceda la velocidad</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={`p-3 rounded-lg border ${
                  alert.alert_type === 'deviation' 
                    ? 'bg-red-100 border-red-300' 
                    : alert.alert_type === 'speed'
                    ? 'bg-orange-100 border-orange-300'
                    : 'bg-green-100 border-green-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {getAlertIcon(alert.alert_type)}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {getAlertBadge(alert.alert_type)}
                        <span className="font-medium text-gray-800">{alert.pilot_name}</span>
                      </div>
                      
                      {alert.alert_type === 'deviation' && (
                        <p className="text-sm text-gray-600">
                          Se ha alejado <strong>{alert.details?.distance_from_track_km?.toFixed(1)} km</strong> del track oficial
                          <br />
                          <span className="text-xs text-gray-500">
                            (umbral configurado: {alert.details?.alert_threshold_km} km)
                          </span>
                        </p>
                      )}
                      
                      {alert.alert_type === 'return_to_track' && (
                        <p className="text-sm text-green-700">
                          ✓ Ha vuelto al track oficial
                          <br />
                          <span className="text-xs text-gray-500">
                            (distancia actual: {alert.details?.distance_from_track_km?.toFixed(1)} km)
                          </span>
                        </p>
                      )}
                      
                      {alert.alert_type === 'speed' && (
                        <p className="text-sm text-gray-600">
                          Velocidad: <strong>{alert.details?.current_speed_kmh?.toFixed(0)} km/h</strong>
                          <br />
                          <span className="text-xs text-gray-500">
                            (límite: {alert.details?.speed_limit_kmh} km/h)
                          </span>
                        </p>
                      )}
                      
                      {alert.alert_type === 'speed_normalized' && (
                        <p className="text-sm text-green-700">
                          ✓ Velocidad normalizada: <strong>{alert.details?.current_speed_kmh?.toFixed(0)} km/h</strong>
                        </p>
                      )}
                      
                      <p className="text-xs text-gray-500 mt-1">
                        {formatTime(alert.created_at)}
                        {alert.details?.latitude && (
                          <a 
                            href={`https://maps.google.com/?q=${alert.details.latitude},${alert.details.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 text-blue-600 hover:underline"
                          >
                            Ver en mapa
                          </a>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  {/* Only show dismiss button for active alerts */}
                  {alert.is_active && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => dismissAlert(alert.id)}
                      disabled={dismissing === alert.id}
                      className="shrink-0"
                      title="Desactivar alerta"
                    >
                      {dismissing === alert.id ? (
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
